import { PenLine, Eye, User, MapPin, Mail, Phone, CalendarDays, Layers, FileText } from "lucide-react";
import { beDate, eur, btnPrimary, btnGhost } from "@/lib/studioApi";

const KIND_LABEL = { fourniture: "Fournitures", main_oeuvre: "Main-d'œuvre", autre: "Autres postes" };
const KIND_CLS = {
  fourniture: "border-amber-200 bg-amber-50 text-amber-800",
  main_oeuvre: "border-emerald-200 bg-emerald-50 text-emerald-800",
  autre: "border-[#E2E7F7] bg-white text-[#5C6A87]",
};
const qty = (v) => Number(v || 0).toLocaleString("fr-BE", { maximumFractionDigits: 2 });

const Info = ({ icon: Icon, children, testId }) => children ? (
  <p data-testid={testId} className="flex items-start gap-2 text-sm text-[#0F2645]"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#93A0BC]" /> <span className="min-w-0 break-words">{children}</span></p>
) : null;

/* Présentation synthétique du devis (lecture seule) : client, prestations, montants. L'édition s'ouvre via « Modifier ». */
export const QuoteSummary = ({ quote, lines, totals, optionNames, chosenOption, optionTotals, kindTotals, detectKind, remarks, conditions, editable, onEdit, onPreview, previewBusy }) => {
  const baseLines = lines.filter((l) => !(l.option || "").trim());
  const address = [quote.site_address, [quote.site_postal_code, quote.site_city].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  const LineRow = ({ l, i }) => (
    <li data-testid={`summary-line-${i}`} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#F3F5FC] font-mono text-[11px] font-bold text-[#5C6A87]">{i + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug text-[#0F2645]">{l.description || <span className="italic text-[#93A0BC]">Description à compléter</span>}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#5C6A87]">
          <span className="font-mono tabular-nums">{qty(l.quantity)} {l.unit}</span>
          <span className="text-[#C9D4F4]">·</span>
          <span className="font-mono tabular-nums">{eur(l.unit_price)} HTVA</span>
          <span className="text-[#C9D4F4]">·</span>
          <span>{l.reverse_charge ? "Autoliquidation" : `TVA ${l.vat_percent} %`}</span>
          <span className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${KIND_CLS[detectKind(l)]}`}>{KIND_LABEL[detectKind(l)]}</span>
        </p>
      </div>
      <p className="shrink-0 font-mono text-sm font-bold tabular-nums text-[#0F2645]">{eur((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</p>
    </li>
  );

  return (
    <section data-testid="quote-summary" className="mt-4 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]" style={{ animation: "devFadeUp .25s ease-out" }}>
      <div className="flex flex-col gap-3 border-b border-[#E2E7F7] p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Présentation du devis</p>
          <h2 className="font-heading mt-0.5 text-lg font-bold text-[#0F2645]">{quote.template_name || "Prestations et montants"}</h2>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="quote-preview-btn" disabled={previewBusy} onClick={onPreview} className={`${btnPrimary} flex-1 sm:flex-none`}>
            <Eye className="h-4 w-4" /> Visualiser le devis
          </button>
          {editable && (
            <button type="button" data-testid="quote-edit-btn" onClick={onEdit} className={`${btnGhost} flex-1 sm:flex-none`}>
              <PenLine className="h-4 w-4 text-[#3A5CC8]" /> Modifier
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0">
          <div className="rounded-xl border border-[#E2E7F7] bg-[#FBFCFE] p-4">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Client et chantier</p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              <Info icon={User} testId="summary-client">{quote.client_name}</Info>
              <Info icon={MapPin} testId="summary-address">{address}</Info>
              <Info icon={Mail}>{quote.client_email}</Info>
              <Info icon={Phone}>{quote.client_phone}</Info>
              <Info icon={CalendarDays} testId="summary-dates">Émis le {beDate(quote.issue_date)} · valable jusqu'au {beDate(quote.valid_until)}</Info>
            </div>
          </div>

          <div className="mt-5">
            <p className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]"><Layers className="h-3.5 w-3.5" /> Prestations {optionNames.length ? "— base" : ""} <span className="ml-auto font-mono normal-case tracking-normal">{baseLines.length} ligne{baseLines.length > 1 ? "s" : ""}</span></p>
            {baseLines.length === 0 ? (
              <div data-testid="summary-empty" className="rounded-xl border border-dashed border-[#C9D4F4] bg-[#F6F8FD] p-6 text-center">
                <FileText className="mx-auto h-6 w-6 text-[#93A0BC]" />
                <p className="mt-2 text-sm font-semibold text-[#0F2645]">Aucune ligne pour l'instant</p>
                <p className="mt-1 text-xs text-[#5C6A87]">Dictez le chantier à l'assistant ou cliquez sur « Modifier » pour ajouter des prestations.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#E2E7F7]">{baseLines.map((l) => <LineRow key={lines.indexOf(l)} l={l} i={lines.indexOf(l)} />)}</ul>
            )}
          </div>

          {optionNames.map((name) => (
            <div key={name} className="mt-5" data-testid={`summary-option-${name}`}>
              <p className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#2F4CA8]">Option {name}{chosenOption === name && <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 normal-case tracking-normal text-emerald-700">retenue par le client</span>}</p>
              <ul className="divide-y divide-[#E2E7F7] rounded-xl border border-[#C9D4F4] bg-[#F8F9FE] px-4 py-1">
                {lines.filter((l) => (l.option || "").trim() === name).map((l) => <LineRow key={lines.indexOf(l)} l={l} i={lines.indexOf(l)} />)}
              </ul>
            </div>
          ))}

          {(remarks || conditions) && (
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {remarks && <div className="rounded-xl bg-[#F6F8FD] p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Remarques</p><p data-testid="summary-remarks" className="mt-1 whitespace-pre-wrap text-sm text-[#0F2645]">{remarks}</p></div>}
              {conditions && <div className="rounded-xl bg-[#F6F8FD] p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Conditions</p><p data-testid="summary-conditions" className="mt-1 line-clamp-6 whitespace-pre-wrap text-sm text-[#0F2645]">{conditions}</p></div>}
            </div>
          )}
        </div>

        <aside className="h-fit rounded-xl border border-[#E2E7F7] bg-[#FBFCFE] p-4 lg:sticky lg:top-24">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Montants</p>
          <dl className="mt-3 space-y-2 text-sm">
            {kindTotals.map((k) => (
              <div key={k.kind} className="flex items-center justify-between text-[#5C6A87]">
                <dt>{KIND_LABEL[k.kind]}</dt><dd className="font-mono tabular-nums">{eur(k.net)}</dd>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-[#E2E7F7] pt-2 text-[#0F2645]"><dt>Total HTVA</dt><dd data-testid="summary-net" className="font-mono font-semibold tabular-nums">{eur(totals.net)}</dd></div>
            <div className="flex items-center justify-between text-[#5C6A87]"><dt>TVA</dt><dd data-testid="summary-vat" className="font-mono tabular-nums">{eur(totals.vat)}</dd></div>
          </dl>
          <div className="mt-3 rounded-xl bg-[#0F2645] px-4 py-3 text-white">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#9FB4F2]">{optionNames.length && !chosenOption ? "Total base TVAC" : "Total TVAC"}</p>
            <p data-testid="summary-total" className="font-mono text-2xl font-extrabold tabular-nums">{eur(totals.net + totals.vat)}</p>
          </div>
          {!chosenOption && optionTotals.map((o) => (
            <p key={o.name} className="mt-2 flex items-center justify-between text-xs text-[#2F4CA8]"><span>Avec option {o.name}</span><span className="font-mono font-bold tabular-nums">{eur(o.net + o.vat)}</span></p>
          ))}
        </aside>
      </div>
    </section>
  );
};
